/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useContext, useEffect, useMemo } from 'react';
import { GlobalContext } from '../../providers/GlobalProvider';
import AuditHeaderLayout from 'components/Common/Layouts/AuditHeaderLayout';
import DocumentsTable from 'components/Monitor/Documents/DocumentsTable';
import ActivityComponent from 'components/Monitor/ActivityTab/ActivityComponent';
import SettingsComponent from 'components/Monitor/SettingsTab/SettingsComponent';
import ResultsComponent from 'components/Monitor/ResultsTab/Results';
import CommentsModal from 'components/Monitor/Modals/CommentsModal';
import DeleteCommonModal from 'components/Monitor/Modals/DeleteModal';
import CasesAPI from 'api/CasesAPI';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { Modal } from '@mui/material';
import AddPersonModal from 'components/Monitor/Modals/AddPersonModal';
import UsersAPI from 'api/UsersAPI';
import SupQuestSubmit from 'components/Monitor/Modals/SupQuestSubmit';
import {
  handleAddCommentNotification,
  handleShareTicketNotification,
} from 'utils/NotificationsFuncs';
import ClassResultsComponent from 'components/Monitor/ResultsTab/ClassResults';
import NotificationsModal from 'components/Monitor/Modals/NotificationModal';
import { compressData, decompressData } from '../../utils/common';
import { IWhitelist } from '../../interfaces/IWhitelist';
import ECCNResultsComponent from 'components/Monitor/ResultsTab/ECCNResults';
import ConsolidatedResults from 'components/Monitor/ResultsTab/ConsolidatedResults';
import DownloadModal from 'components/Monitor/Modals/DownloadModal';
import OwnershipTree from 'components/BeneficialOwnership/OwnershipTree';
import { hasOwnershipGraph } from 'utils/beneficialOwnershipGraph';
import { canBuildBeneficialOwnershipVisual } from 'utils/beneficialOwnershipVisualData';
import ExportDesignTicket from 'components/ExportDesign/ExportDesignTicket';
import { readDesignTicket } from 'components/ExportDesign/exportDesignTicketPayload';
import { canScheduleTicket } from 'utils/reScreening';

const Ticket: React.FC = (props: any) => {
  const { cur_user } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const dt = new Date();
  const dtWithoutTz = new Date(
    dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000,
  );
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');
  const [trigger, setTrigger] = useState(false);
  const [ticket_id, setTicketID] = useState(props.match.params.ticket_id);
  const [autoTab, setAutoTab] = useState<string>('Results');

  const [downloadOpts, setDownloadOpts] = useState<string>('PDF');
  const [shareModal, setShareModal] = useState(false);
  const [downloadModal, setDownloadModal] = useState(false);
  const [shareModalMode, setShareModalMode] = useState<'share' | 'send'>(
    'share',
  );
  const [owner, setOwner] = useState('');
  const [triggerCSV, setTriggerCSV] = useState(false);
  const [triggerSendPdf, setTriggerSendPdf] = useState(false);
  const [triggerRescreen, setTriggerRescreen] = useState(false);
  const [supQuestModal, setSupQuestModal] = useState(false);
  const [userHasMonitor, setUserHasMonitor] = useState(true);
  const [isNotification, setIsNotification] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isBulkTicket, setIsBulkTicket] = useState(false);
  const [whiteListLoading, setWhiteListLoading] = useState(false);
  const [cur_case, setCase] = useState<{
    isLoaded: boolean;
    error: string | undefined;
    data: any;
  }>({ isLoaded: false, error: undefined, data: {} });

  // Beneficial Ownership is the only docType with an Ownership Tree tab, and
  // only when the run actually saved a graph — an empty diagram is worse than
  // no tab. Every other ticket type sees the tab bar exactly as before.
  const isBeneficialOwnershipTicket =
    cur_case.data?.docType === 'Beneficial Ownership';
  // Memoised: building the graph walks every entity in the saved pipeline,
  // and Ticket re-renders on a lot of unrelated state.
  const showOwnershipTreeTab = useMemo(
    () => isBeneficialOwnershipTicket && hasOwnershipGraph(cur_case.data),
    [isBeneficialOwnershipTicket, cur_case.data],
  );
  // The visual summary is offered only when the report has a network to map.
  const boVisualUnavailable = useMemo(
    () =>
      isBeneficialOwnershipTicket &&
      !canBuildBeneficialOwnershipVisual(cur_case.data),
    [isBeneficialOwnershipTicket, cur_case.data],
  );

  // Settings controls the re-screen/re-price schedule, so it is meaningful only for ticket
  // types that actually have one: entity/product re-screens AND US tariff monitors (Setting.tsx
  // already branches on both via isMonitoredTariffTicket). AI-driven one-shot tickets
  // (Beneficial Ownership, HTS Classification, Export Workflow, ECCN Check, Design Assistant,
  // Import Workflow, ...) have no schedule at all. Same helper Setting.tsx itself uses to decide
  // whether to show real controls vs "N/A", so the tab's visibility can't drift from its content.
  const showRescreen = canScheduleTicket(
    cur_case.data?.docType,
    cur_case.data?.tariffJurisdiction,
  );

  // The Ticket component instance is reused across ticket navigation (only the
  // route param changes), so `autoTab` can stay on 'Ownership Tree' after moving
  // to a ticket that has no tree. With that tab hidden, no tab body matches and
  // the page renders blank — so fall the selection back to Results whenever the
  // Ownership Tree tab is not available.
  useEffect(() => {
    if (autoTab === 'Ownership Tree' && !showOwnershipTreeTab) {
      setAutoTab('Results');
      window.sessionStorage.setItem('autoTab', 'Results');
    }
    if (autoTab === 'Settings' && !showRescreen) {
      setAutoTab('Results');
      window.sessionStorage.setItem('autoTab', 'Results');
    }
  }, [autoTab, showOwnershipTreeTab, showRescreen]);

  const [fileName, setFileName] = useState<string>(
    cur_case.data.ticketName ? cur_case.data.ticketName : '',
  );
  const [sendPdfFileName, setSendPdfFileName] = useState<string>('');
  const [sendPdfEmailRecipients, setSendPdfEmailRecipients] = useState<
    string[]
  >([]);

  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedOptsType, setSelectedOptsType] = useState([]);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState<number>(0);

  const handleTabSwitch = (newTab: string) => {
    switch (newTab) {
      case 'Results':
        setAutoTab('Results');
        window.sessionStorage.setItem('autoTab', 'Results');
        break;
      case 'Ownership Tree':
        setAutoTab('Ownership Tree');
        window.sessionStorage.setItem('autoTab', 'Ownership Tree');
        break;
      case 'Documents':
        setAutoTab('Documents');
        window.sessionStorage.setItem('autoTab', 'Documents');
        break;
      case 'Activity':
        setAutoTab('Activity');
        window.sessionStorage.setItem('autoTab', 'Activity');
        break;
      case 'Settings':
        setAutoTab('Settings');
        window.sessionStorage.setItem('autoTab', 'Settings');
        break;
      default:
        setAutoTab('Results');
    }
  };

  const handleRescreen = () => {
    setAutoTab('Results');
    setTriggerRescreen(true);
  };

  const handleResultsDownload = (payload?: { name?: string }) => {
    if (autoTab !== 'Results') {
      enqueueSnackbar('You need to be on the Results tab to download.', {
        variant: 'info',
      });
      return;
    }

    setTriggerCSV(true);
  };

  const handleSendTariffPdf = (payload?: {
    name?: string;
    emails?: string[];
  }) => {
    if (autoTab !== 'Results') {
      enqueueSnackbar('You need to be on the Results tab to send.', {
        variant: 'info',
      });
      return;
    }

    setSendPdfFileName(payload?.name || '');
    setSendPdfEmailRecipients(payload?.emails || []);
    setTriggerSendPdf(true);
  };

  const handleDownloadModalClose = () => {
    setDownloadModal(false);
    setDownloadOpts('PDF');
    setFileName('');
  };

  // A Beneficial Ownership download closes the dialog itself, so the choice
  // and the typed name reset here rather than carry into the next download.
  useEffect(() => {
    if (!downloadModal && isBeneficialOwnershipTicket) {
      setDownloadOpts('PDF');
      setFileName('');
    }
  }, [downloadModal, isBeneficialOwnershipTicket]);

  const handleShareModalClose = () => {
    setShareModal(false);
    setShareModalMode('share');
    setSendPdfFileName('');
    setSendPdfEmailRecipients([]);
  };

  const handleOpenShareModal = () => {
    setShareModalMode('share');
    setShareModal(true);
  };

  const handleOpenSendPdfModal = () => {
    setShareModalMode('send');
    setShareModal(true);
  };

  const handleTariffPdfSendComplete = (success: boolean) => {
    if (success) {
      handleShareModalClose();
    }
  };

  useEffect(() => {
    checkForMonitor();
  }, []);

  const requiredTiers = ['tariffs', 'starter', 'pro', 'business', 'enterprise'];

  const checkForMonitor = () => {
    if (!requiredTiers.includes(cur_user.license)) {
      setUserHasMonitor(false);
    }
  };

  const handleDeleteTicket = async () => {
    const resp = await CasesAPI.deleteCase(cur_case.data._id);
    if (resp?.success) {
      enqueueSnackbar('Removed case', { variant: 'info' });
      history.push('/bite-monitor');
    } else if (resp) {
      const error_msg = `Error removing ticket: ${resp?.result}`;
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
  };

  const favoriteTicket = async () => {
    const update: any = {
      _id: cur_case.data._id,
      favorite: !cur_case.data.favorite,
    };
    await CasesAPI.updateCase(update);
    setTrigger(!trigger);
  };

  const whitelistHandler = async (content: any) => {
    const value = content as IWhitelist;
    const newRecord: IWhitelist = {
      ...value,
      ticket_name: cur_case.data.ticketName,
      ticket_id: cur_case.data._id,
      timestamp: formatted_dt,
    };

    try {
      if (whiteListLoading) {
        enqueueSnackbar('Whitelist in progress', { variant: 'info' });
        return;
      }

      setWhiteListLoading(true);

      let decompressedWhitelist: IWhitelist[] = [];
      if (cur_case.data.whitelisted && cur_case.data.whitelisted.length > 0) {
        decompressedWhitelist = decompressData(cur_case.data.whitelisted);
      }

      const existingRecordIndex = decompressedWhitelist.findIndex(
        (item: IWhitelist) => item.field === newRecord.field,
      );

      // eslint-disable-next-line init-declarations
      let updatedDecompressedWhitelist: IWhitelist[];
      // eslint-disable-next-line init-declarations
      let successMessage: string;

      if (existingRecordIndex !== -1) {
        updatedDecompressedWhitelist = decompressedWhitelist.filter(
          (_, index) => index !== existingRecordIndex,
        );
        successMessage = `Successfully removed entity: ${newRecord.field}`;
      } else {
        updatedDecompressedWhitelist = [...decompressedWhitelist, newRecord];
        successMessage = `Successfully whitelisted entity: ${newRecord.field}`;
      }

      const compressedWhitelist = compressData(updatedDecompressedWhitelist);

      const updatePayload: any = {
        _id: cur_case.data._id,
        whitelisted: compressedWhitelist,
      };

      await CasesAPI.updateCase(updatePayload);
      enqueueSnackbar(successMessage, { variant: 'success' });
      setTrigger(!trigger);
    } catch (e) {
      enqueueSnackbar(
        `Error processing whitelist for entity: ${newRecord.field}`,
        { variant: 'error' },
      );
    } finally {
      setWhiteListLoading(false);
    }
  };

  const notificationTicket = async () => {
    // An empty preference reads as "never set" (both channels), so turning
    // every channel off is saved as an explicit ['None'].
    const chosen = (selectedOptsType || []).filter(
      (opt: string) => opt !== 'None',
    );
    const update: any = {
      _id: cur_case.data._id,
      notOptions: chosen.length ? chosen : ['None'],
    };
    await CasesAPI.updateCase(update);
    setIsNotificationOpen(false);
    setTrigger(!trigger);
  };

  const addComment = async (content: string) => {
    const comment: any = {
      content: content,
      person: `${cur_user?.basics?.firstName} ${cur_user?.basics?.lastName}`,
      date: formatted_dt,
    };
    const actObj: any = {
      user: {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      },
      action: 'added a comment',
      timeStamp: Date.now(),
    };
    const update: any = {
      _id: cur_case.data._id,
      comments: [...cur_case.data.comments, comment],
      activity: [...cur_case.data.activity, actObj],
    };
    const res = await CasesAPI.updateCase(update);
    if (res?.success) {
      const currentEmail = cur_user.basics?.email;
      const ownerEmail = cur_case.data.owner?.email;

      // iterate over sharedWith
      cur_case.data.sharedWith.forEach((email: string) => {
        if (email !== currentEmail) {
          // don’t notify the current user
          handleAddCommentNotification(
            cur_case.data.ticketName,
            cur_user,
            email,
            cur_case.data._id,
          );
        }
      });

      // If current user is in sharedWith but not the owner, also notify the owner
      if (
        cur_case.data.sharedWith.includes(currentEmail) &&
        currentEmail !== ownerEmail &&
        ownerEmail
      ) {
        handleAddCommentNotification(
          cur_case.data.ticketName,
          cur_user,
          ownerEmail,
          cur_case.data._id,
        );
      }
    } else {
      const error_msg = 'Error adding comments.';
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
    setTrigger(!trigger);
  };

  const addShared = async (emails: any) => {
    const actObj: any = {
      user: {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      },
      action: `shared with ${emails}`,
      timeStamp: Date.now(),
    };
    const update: any = {
      _id: cur_case.data._id,
      sharedWith: [...cur_case.data.sharedWith, ...emails],
      activity: [...cur_case.data.activity, actObj],
    };
    const res = await CasesAPI.updateCase(update);
    if (res?.success) {
      emails.forEach((email: string) => {
        // Call to notification sending function here
        handleShareTicketNotification(
          cur_case.data.ticketName,
          cur_user,
          email,
          cur_case.data._id,
        );
      });
      enqueueSnackbar('Successfully shared.', { variant: 'success' });
    } else {
      const error_msg = 'Error sharing.';
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
    setTrigger(!trigger);
  };

  const removeComment = async (arr: any) => {
    const actObj: any = {
      user: {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      },
      action: 'removed a comment',
      timeStamp: Date.now(),
    };
    const update: any = {
      _id: cur_case.data._id,
      comments: [...arr],
      activity: [...cur_case.data.activity, actObj],
    };
    await CasesAPI.updateCase(update);
    setTrigger(!trigger);
  };

  const helpContent = (
    <p className='module-header-tooltip-text'>
      Review the results or manage any aspect of your ticket from this page. The
      tabs will take you to new information or functionality.
    </p>
  );
  const getOwner = async (id: string) => {
    const res = await UsersAPI.getUser(id);
    if (res?.success) {
      setOwner(res?.result.basics.email);
    }
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedOptsType.length > 0) {
      setIsNotificationOpen(true);
    } else {
      // setIsNotification(!isNotification);
      setIsNotificationOpen(!isNotificationOpen);
    }
  };

  useEffect(() => {
    if (ticket_id) {
      CasesAPI.getCase(ticket_id)?.then((res: any) => {
        if (res?.success) {
          if (
            res.result.owner?.email !== cur_user.basics?.email &&
            !res.result.sharedWith.includes(cur_user.basics?.email) &&
            !res.result.sharedWith.includes(cur_user.basics?.id) &&
            !(res.result.createdBy === cur_user.basics?.id)
          ) {
            history.push('/');
          }
          // 'Customs Entry Analysis' has no results renderer here yet -- its
          // "detail view" is the live, resumable workflow in the Customs
          // Entry Analyzer itself, so send the user straight there instead
          // of showing a blank/generic ticket page. The Case schema has no
          // free-form "link" field, so the batch reference travels in
          // workflowData[0] (see CustomsEntryAnalyzer.tsx's handleSaveAsTicket).
          const customsBatchId = res.result.workflowData?.[0]?.documentBatchId;
          if (res.result.docType === 'Customs Entry Analysis' && customsBatchId) {
            history.push(`/customs-entry-analyzer?batch=${customsBatchId}`);
            return;
          }
          setCase({ isLoaded: true, error: undefined, data: res.result });
          setSelectedOptsType(res.result.notOptions);
          if (res.result.owner) {
            setOwner(res.result.owner?.email);
          } else {
            getOwner(res.result.createdBy);
          }
          if (
            res.result.docType === 'Supplier Questionnaire' &&
            res.result.supQuestReady &&
            !(res.result.results?.length > 0)
          ) {
            setSupQuestModal(true);
          }
        } else {
          const err_text = `Error loading case: ${res?.result}`;
          enqueueSnackbar(err_text, { variant: 'error' });
        }
      });
    } else {
      setCase({ isLoaded: true, error: undefined, data: {} });
    }
  }, [ticket_id, trigger]);

  useEffect(() => {
    if (selectedOptsType.length > 0) {
      setIsNotification(true);
    } else {
      setIsNotification(false);
    }
  }, [selectedOptsType]);

  useEffect(() => {
    if (cur_case.data && typeof cur_case.data.docType === 'string') {
      if (cur_case.data.docType.toLowerCase().includes('bulk')) {
        setIsBulkTicket(true);
      } else {
        setIsBulkTicket(false);
      }
    } else {
      setIsBulkTicket(false);
    }
  }, [cur_case.data]);

  if (!cur_case.isLoaded) {
    return (
      <AuditHeaderLayout
        title={''}
        helpContent={helpContent}
        fav={false}
        shareModal={false}
        downloadModal={false}
        setShareModal={setShareModal}
        cur_case={cur_case}
        isNotification={isNotification}
      >
        <div className='spinner-spacer-table'>
          <div className='loader'>Loading...</div>
        </div>
      </AuditHeaderLayout>
    );
  }

  return (
    <AuditHeaderLayout
      title={cur_case.data.ticketName}
      helpContent={helpContent}
      handleTooltipClick={() => setIsCommentsModalOpen(true)}
      handleDeleteClick={() => setIsDeleteModalOpen(true)}
      handleFavorite={favoriteTicket}
      fav={cur_case.data.favorite}
      handleCSV={handleResultsDownload}
      handleOpenShareModal={handleOpenShareModal}
      triggerCSV={triggerCSV}
      triggerSendPdf={triggerSendPdf}
      shareModal={shareModal}
      setShareModal={setShareModal}
      downloadModal={downloadModal}
      setDownloadModal={setDownloadModal}
      cur_case={cur_case}
      userHasMonitor={userHasMonitor}
      isNotification={isNotification}
      handleSwitchChange={handleSwitchChange}
      totalComments={cur_case.data.comments.length || 0}
      handleRescreen={handleRescreen}
      triggerRescreen={triggerRescreen}
      showRescreen={showRescreen}
    >
      <div className='screening-tabs-container'>
        <div className='screening-tabs-inner'>
          <div
            className={`screening-tab-btn${
              autoTab === 'Results' ? '-selected' : ''
            }`}
            onClick={() => handleTabSwitch('Results')}
          >
            <div
              className={`screening-tab-btn-text${
                autoTab === 'Results' ? '-selected' : ''
              }`}
            >
              Results
            </div>
          </div>
          {showOwnershipTreeTab && (
            <div
              className={`screening-tab-btn${
                autoTab === 'Ownership Tree' ? '-selected' : ''
              }`}
              onClick={() => handleTabSwitch('Ownership Tree')}
            >
              <div
                className={`screening-tab-btn-text${
                  autoTab === 'Ownership Tree' ? '-selected' : ''
                }`}
              >
                Relationship Tree
              </div>
            </div>
          )}
          <div
            className={`screening-tab-btn${
              autoTab === 'Documents' ? '-selected' : ''
            }`}
            onClick={() => handleTabSwitch('Documents')}
          >
            <div
              className={`screening-tab-btn-text${
                autoTab === 'Documents' ? '-selected' : ''
              }`}
            >
              Documents
            </div>
          </div>
          <div
            className={`screening-tab-btn${
              autoTab === 'Activity' ? '-selected' : ''
            }`}
            onClick={() => handleTabSwitch('Activity')}
          >
            <div
              className={`screening-tab-btn-text${
                autoTab === 'Activity' ? '-selected' : ''
              }`}
            >
              Activity
            </div>
          </div>
          {showRescreen && (
            <div
              className={`screening-tab-btn${
                autoTab === 'Settings' ? '-selected' : ''
              }`}
              onClick={() => handleTabSwitch('Settings')}
            >
              <div
                className={`screening-tab-btn-text${
                  autoTab === 'Settings' ? '-selected' : ''
                }`}
              >
                Settings
              </div>
            </div>
          )}
        </div>
      </div>
      {autoTab === 'Ownership Tree' && showOwnershipTreeTab && (
        <OwnershipTree ticket={cur_case.data} />
      )}
      {autoTab === 'Results' && (
        <>
          {['Classification', 'ECCN'].includes(cur_case.data.docType) ? (
            <ClassResultsComponent
              cur_user={cur_user}
              cur_case={cur_case}
              trigger={trigger}
              setTrigger={setTrigger}
              triggerCSV={triggerCSV}
              setTriggerCSV={setTriggerCSV}
              triggerSendPdf={triggerSendPdf}
              setTriggerSendPdf={setTriggerSendPdf}
              sendPdfFileName={sendPdfFileName}
              sendPdfEmailRecipients={sendPdfEmailRecipients}
              onTariffPdfSendComplete={handleTariffPdfSendComplete}
            />
          ) : ['Control Checker'].includes(cur_case.data.docType) ? (
            <ECCNResultsComponent
              cur_user={cur_user}
              cur_case={cur_case}
              trigger={trigger}
              setTrigger={setTrigger}
              triggerCSV={triggerCSV}
              setTriggerCSV={setTriggerCSV}
              triggerSendPdf={triggerSendPdf}
              setTriggerSendPdf={setTriggerSendPdf}
              sendPdfFileName={sendPdfFileName}
              sendPdfEmailRecipients={sendPdfEmailRecipients}
              onTariffPdfSendComplete={handleTariffPdfSendComplete}
            />
          ) : ['ECCN Check'].includes(cur_case.data.docType) ? (
            <ECCNResultsComponent
              cur_user={cur_user}
              cur_case={cur_case}
              trigger={trigger}
              setTrigger={setTrigger}
              triggerCSV={triggerCSV}
              setTriggerCSV={setTriggerCSV}
              triggerSendPdf={triggerSendPdf}
              setTriggerSendPdf={setTriggerSendPdf}
              sendPdfFileName={sendPdfFileName}
              sendPdfEmailRecipients={sendPdfEmailRecipients}
              onTariffPdfSendComplete={handleTariffPdfSendComplete}
            />
          ) : ['Import Unified'].includes(cur_case.data.docType) ? (
            <ConsolidatedResults
              cur_user={cur_user}
              cur_case={cur_case}
              trigger={trigger}
              setTrigger={setTrigger}
              triggerCSV={triggerCSV}
              setTriggerCSV={setTriggerCSV}
              triggerSendPdf={triggerSendPdf}
              setTriggerSendPdf={setTriggerSendPdf}
              sendPdfFileName={sendPdfFileName}
              sendPdfEmailRecipients={sendPdfEmailRecipients}
              onTariffPdfSendComplete={handleTariffPdfSendComplete}
              whitelistEntity={whitelistHandler}
              setShareModal={setShareModal}
              setIsCommentsModalOpen={setIsCommentsModalOpen}
            />
          ) : ['Export Unified'].includes(cur_case.data.docType) ? (
            <ConsolidatedResults
              cur_user={cur_user}
              cur_case={cur_case}
              trigger={trigger}
              setTrigger={setTrigger}
              triggerCSV={triggerCSV}
              setTriggerCSV={setTriggerCSV}
              triggerSendPdf={triggerSendPdf}
              setTriggerSendPdf={setTriggerSendPdf}
              sendPdfFileName={sendPdfFileName}
              sendPdfEmailRecipients={sendPdfEmailRecipients}
              onTariffPdfSendComplete={handleTariffPdfSendComplete}
              whitelistEntity={whitelistHandler}
              setShareModal={setShareModal}
              setIsCommentsModalOpen={setIsCommentsModalOpen}
            />
          ) : ['Design Assistant'].includes(cur_case.data.docType) ? (
            // isPreview=false: suppresses ExportDesignTicket's own mock banner/title/tab bar,
            // since AuditHeaderLayout + this page's own tab bar (above) already provide that
            // chrome for real. triggerCSV/setTriggerCSV are the same state every other branch
            // here gets, wired the same way (flip -> generate PDF -> reset).
            <ExportDesignTicket
              ticket={readDesignTicket(cur_case.data)}
              isPreview={false}
              triggerCSV={triggerCSV}
              setTriggerCSV={setTriggerCSV}
            />
          ) : (
            <ResultsComponent
              cur_user={cur_user}
              cur_case={cur_case}
              trigger={trigger}
              setTrigger={setTrigger}
              setShareModal={setShareModal}
              setIsCommentsModalOpen={setIsCommentsModalOpen}
              triggerCSV={triggerCSV}
              triggerSendPdf={triggerSendPdf}
              downloadOpts={downloadOpts}
              fileName={fileName}
              sendPdfFileName={sendPdfFileName}
              sendPdfEmailRecipients={sendPdfEmailRecipients}
              onTariffPdfSendComplete={handleTariffPdfSendComplete}
              onViewOwnershipTree={() => handleTabSwitch('Ownership Tree')}
              whitelistEntity={whitelistHandler}
              isBulkTicket={isBulkTicket}
              setTriggerCSV={setTriggerCSV}
              setTriggerSendPdf={setTriggerSendPdf}
              selectedVersionIndex={selectedVersionIndex}
              setDownloadModal={setDownloadModal}
              triggerRescreen={triggerRescreen}
              setTriggerRescreen={setTriggerRescreen}
            />
          )}
        </>
      )}
      {autoTab === 'Documents' && (
        <>
          <DocumentsTable
            tableName={'Ticket Documents'}
            cur_case={cur_case}
            trigger={trigger}
            setTrigger={setTrigger}
          />
        </>
      )}
      {autoTab === 'Activity' && (
        <ActivityComponent
          cur_case={cur_case}
          handleTabSwitch={handleTabSwitch}
          owner={owner}
          setShareModal={setShareModal}
          selectedVersionIndex={selectedVersionIndex}
          setSelectedVersionIndex={setSelectedVersionIndex}
          showVersionHistory={showRescreen}
        />
      )}

      {showRescreen && autoTab === 'Settings' && (
        <SettingsComponent
          cur_case={cur_case}
          trigger={trigger}
          setTrigger={setTrigger}
          setAutoTab={setAutoTab}
          cur_user={cur_user}
        />
      )}
      <Modal
        open={isCommentsModalOpen}
        onClose={() => setIsCommentsModalOpen(false)}
      >
        <CommentsModal
          handleTooltipClick={() => setIsCommentsModalOpen(false)}
          comments={cur_case.data.comments}
          addComment={addComment}
          removeComment={removeComment}
          cur_user={cur_user}
          cur_case={cur_case}
          trigger={trigger}
          setTrigger={setTrigger}
        />
      </Modal>
      <Modal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <DeleteCommonModal
          handleClose={() => setIsDeleteModalOpen(false)}
          handleDelete={handleDeleteTicket}
          title={'Delete?'}
          paragraph={
            'Once Deleted there is no way to recover this or the infomation saved on it.'
          }
        />
      </Modal>
      <Modal
        open={shareModal}
        onClose={() => {
          if (!triggerSendPdf) {
            handleShareModalClose();
          }
        }}
      >
        <AddPersonModal
          cur_user={cur_user}
          handleClose={handleShareModalClose}
          sharedArr={cur_case.data.sharedWith}
          owner={owner}
          addShared={addShared}
          mode={shareModalMode}
          handleSend={handleSendTariffPdf}
          triggerSend={triggerSendPdf}
          isOpen={shareModal}
        />
      </Modal>
      <Modal open={downloadModal} onClose={handleDownloadModalClose}>
        <DownloadModal
          handleClose={handleDownloadModalClose}
          setFileName={setFileName}
          downloadOpts={downloadOpts}
          setDownloadOpts={setDownloadOpts}
          isOpen={downloadModal}
          handleCSV={handleResultsDownload}
          triggerCSV={triggerCSV}
          mode={isBeneficialOwnershipTicket ? 'beneficial_ownership' : 'default'}
          visualUnavailable={boVisualUnavailable}
        />
      </Modal>
      <Modal open={supQuestModal} onClose={() => setSupQuestModal(false)}>
        <SupQuestSubmit
          cur_case={cur_case.data}
          handleClose={() => setSupQuestModal(false)}
          handleTabSwitch={handleTabSwitch}
        />
      </Modal>
      <Modal
        open={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
      >
        <NotificationsModal
          selectedOptsType={selectedOptsType}
          setSelectedOptsType={setSelectedOptsType}
          handleClose={() => setIsNotificationOpen(false)}
          handleSave={notificationTicket}
        />
      </Modal>
    </AuditHeaderLayout>
  );
};

export default Ticket;
